import {inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormArray, FormControl, NonNullableFormBuilder, Validators,} from '@angular/forms';
import {Property} from '../../models/property.model';
import {ObjectOwner} from '../../models/object-owner.model';
import {PowerOfAttorneyInformation} from '../../models/power-of-attorney-information.model';
import {UploadedAuthorityDocument} from '../../models/uploaded-authority-document.model';
import {User} from '../../models/user.model';
import {Landregisterfolio} from '../../models/landregisterfolio.model';
import {Flur} from '../../models/flur.model';
import {Flurstueck} from '../../models/flurstueck.model';
import {OrderFormDraftDto} from '../../models/order-form-draft';
import {MailVerificationDto} from '../../models/mail-verification.model';
import {BillingAddress} from '../../models/billing-address.model';
import {DirectBookingEntryParameters} from '../../models/direct-booking-entry-parameters.model';
import {
  atLeastOneNonBlankFieldValidator,
  completeEmailDomainValidator,
  containsDigitValidator,
  countryPostalCodeValidator,
  houseNumberValidator,
  inheritanceDocumentsAvailableValidator,
  meaningfulTextValidator,
  nonBlankValidator,
  nonEmptyStringArrayValidator,
  notInFutureDateValidator,
  personNameValidator,
  phoneNumberValidator,
} from '../../utils/form-utils';
import {DEFAULT_COUNTRY_NAME} from '../../utils/country-options';
import {normalizeString} from '../../utils/string-utils';
import {ObjectOwnerType} from '../../models/enums/object-owner-type';
import {ParcelGeometry} from '../../models/parcel-geometry';

@Injectable({ providedIn: 'root' })
export class OrderFormFactoryService {
  private readonly formBuilder = inject(NonNullableFormBuilder);

  public readonly orderForm = this.create();
  public readonly userForm = this.orderForm.controls.user;
  public readonly objectOwnerForm = this.orderForm.controls.objectOwner;
  public readonly powerOfAttorneyInformationForm =
    this.orderForm.controls.powerOfAttorneyInformation;
  public readonly mailVerificationForm = this.orderForm.controls.mailVerification;
  public readonly billingAddressForm = this.orderForm.controls.billingAddress;
  public readonly entryParametersForm = this.orderForm.controls.entryParameters;
  public readonly property = signal<Property | null>(null);
  public readonly entryParameters = signal<DirectBookingEntryParameters>(
    this.entryParametersForm.getRawValue(),
  );
  private readonly userMailSignal = signal(this.userForm.controls.mail.value);
  /**
   * Current service-provider email as a signal. Stays in sync with live edits and with silent
   * draft restores (`reset(..., {emitEvent: false})`) via {@link syncDerivedState}, so reactive
   * consumers see the value even when `valueChanges` does not fire.
   */
  public readonly userMail = this.userMailSignal.asReadonly();

  /**
   * Stores a pending mail verification so it can be restored from the local draft.
   *
   * @param id Verification id returned by the backend.
   * @param mail Email address used for the verification request.
   */
  public setPendingMailVerification(id: string, mail: string): void {
    this.updateMailVerification({
      id,
      email: mail,
      status: 'PENDING',
    });
  }

  /**
   * Stores a verified mail verification and marks the mail as trusted for the current form.
   *
   * @param id Verification id returned by the backend.
   * @param mail Verified email address returned by the backend.
   */
  public setVerifiedMailVerification(id: string, mail: string): void {
    this.updateMailVerification({
      id,
      email: mail,
      status: 'VERIFIED',
    });
  }

  /**
   * Clears the stored mail verification when it no longer belongs to the current user mail.
   */
  public clearMailVerification(): void {
    this.updateMailVerification({
      id: '',
      email: '',
      status: null,
    });
  }

  /**
   * Returns the currently persisted mail verification values.
   *
   * @returns Stored mail verification state from the central order form.
   */
  public getMailVerification(): MailVerificationDto {
    return this.mailVerificationForm.getRawValue() as MailVerificationDto;
  }

  /**
   * Checks whether the stored verification belongs to the provided mail address.
   *
   * @param mail Email address to compare against the persisted verification.
   * @returns `true` when the persisted verification has the same normalized mail.
   */
  public isMailVerificationForMail(mail: string): boolean {
    const verification = this.getMailVerification();

    return (!!verification.email && normalizeString(verification.email) === normalizeString(mail));
  }

  /**
   * Checks whether the current service provider mail address was verified.
   *
   * @returns `true` when the user form mail matches the verified mail address.
   */
  public isCurrentUserMailVerified(): boolean {
    const currentMail = normalizeString(this.userForm.controls.mail.value);
    const verification = this.getMailVerification();

    return (!!currentMail && verification.status === 'VERIFIED' && normalizeString(verification.email ?? '') === currentMail);
  }

  constructor() {
    this.configureObjectOwnerTypeValidators();
    this.configurePowerOfAttorneyValidators();
    this.userForm.controls.country.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.userForm.controls.zip.updateValueAndValidity();
    });
    this.userForm.controls.mail.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((mail) => this.userMailSignal.set(mail));
  }

  /**
   * Re-synchronizes derived state after the form was populated with `emitEvent: false`.
   *
   * Silent draft restores do not trigger value-change subscriptions. The postal-code controls
   * therefore need to be revalidated after their associated country controls have been restored.
   */
  public syncDerivedState(): void {
    this.userMailSignal.set(this.userForm.controls.mail.value);
    this.userForm.controls.zip.updateValueAndValidity({emitEvent: false});
    this.billingAddressForm.controls.plz.updateValueAndValidity({emitEvent: false});
  }

  /**
   * Stores the selected property and rebuilds the property sub-form from that data.
   *
   * @param property Property selected or returned by the parcel map.
   */
  public updateProperty(property: Property): void {
    this.property.set(property);
    this.orderForm.setControl('property', this.createPropertyForm(property));
  }

  /**
   * Stores direct-booking entry parameters in both the reactive form and the mirrored signal.
   *
   * The form writes stay silent (`emitEvent: false`) to match the rest of the factory, so the
   * signal is the reactive source consumers should read from.
   *
   * @param entryParameters Entry parameters to persist in the central order form.
   */
  public updateEntryParameters(entryParameters: DirectBookingEntryParameters): void {
    const value = {
      coupon: entryParameters.coupon ?? '',
      packageUuid: entryParameters.packageUuid ?? '',
    };

    this.entryParametersForm.setValue(value, {emitEvent: false});
    this.entryParameters.set(value);
  }

  /**
   * Clears the selected property and resets the property sub-form to its empty shape.
   */
  public clearProperty(): void {
    this.property.set(null);
    this.orderForm.setControl('property', this.createPropertyForm());
  }

  /**
   * Resets the current in-memory order form to its initial empty state.
   */
  public reset(): void {
    const emptyForm = this.create();

    this.orderForm.reset(emptyForm.getRawValue(), {emitEvent: false});
    this.updatePowerOfAttorneyInformation();
    this.refreshObjectOwnerValidators();
    this.clearProperty();
    this.syncDerivedState();
    this.entryParameters.set(emptyForm.controls.entryParameters.getRawValue());
  }

  /**
   * Rebuilds the power-of-attorney sub-form from restored draft data.
   *
   * This preserves the existing sub-form reference while rebuilding dynamic array
   * controls, because components and validators keep references to the original form.
   *
   * @param powerOfAttorneyInformation Optional restored values used to prefill the sub-form.
   */
  public updatePowerOfAttorneyInformation(powerOfAttorneyInformation?: PowerOfAttorneyInformation): void {
    const value = powerOfAttorneyInformation ?? {
      uploadedAuthorityTemporaryUploadIds: [],
      uploadedAuthorityDocuments: [],
      uploadedInheritanceTemporaryUploadIds: [],
      uploadedInheritanceDocuments: [],
    };
    const controls = this.powerOfAttorneyInformationForm.controls;

    this.replaceStringArrayControls(controls.uploadedAuthorityTemporaryUploadIds, value.uploadedAuthorityTemporaryUploadIds ?? []);
    this.setUploadedAuthorityDocuments(value.uploadedAuthorityDocuments ?? []);
    controls.isPowerOfAttorneyAvailable.setValue(value.isPowerOfAttorneyAvailable ?? null, {
      emitEvent: false,
    });

    this.replaceStringArrayControls(controls.uploadedInheritanceTemporaryUploadIds, value.uploadedInheritanceTemporaryUploadIds ?? [],);
    this.setUploadedInheritanceDocuments(value.uploadedInheritanceDocuments ?? []);
    controls.submissionId.setValue(value.submissionId ?? '', { emitEvent: false });
    controls.submissionUUID.setValue(value.submissionUUID ?? '', { emitEvent: false });

    this.refreshPowerOfAttorneyValidators();
  }

  /**
   * Removes one parcel from the centrally stored property and refreshes the property sub-form.
   *
   * @param folioIndex Index of the parent land register folio.
   * @param flurIndex Index of the parent flur within the folio.
   * @param flurstueckIndex Index of the parcel within the flur.
   */
  public removeParcel(folioIndex: number, flurIndex: number, flurstueckIndex: number): void {
    const property = this.property();
    if (!property) {
      return;
    }

    const landregisterfolios = property.landregisterfolios
      .map((folio, currentFolioIndex) => {
        if (currentFolioIndex !== folioIndex) {
          return folio;
        }

        return this.removeParcelFromFolio(folio, flurIndex, flurstueckIndex);
      })
      .filter((folio): folio is Landregisterfolio => !!folio);

    this.updateProperty({ ...property, landregisterfolios });
  }

  /**
   * Keeps only folios, flure, and parcels that the map reported as valid.
   *
   * @param payload Valid parcel identifiers and optional property correlation id.
   */
  public filterPropertyFoliosByParcels({
    validParcels,
    propertyTempId,
  }: {
    validParcels: string[];
    propertyTempId?: string;
  }): void {
    const property = this.property();
    if (!property || (propertyTempId && property.tempId && propertyTempId !== property.tempId)) {
      return;
    }

    const parcelSet = new Set(validParcels);
    const filteredProperty: Property = {
      ...property,
      landregisterfolios: property.landregisterfolios
        .map((folio) => this.filterFolioByParcels(folio, parcelSet))
        .filter((folio): folio is Landregisterfolio => !!folio),
    };

    this.updateProperty(filteredProperty);
  }

  /**
   * Creates the root reactive form for the draft data.
   * The returned form contains nested sub-forms for property, object owner,
   * power-of-attorney information, and user data.
   *
   * @param draft Optional initial values used to prefill the nested sub-forms.
   */
  public create(draft?: OrderFormDraftDto) {
    return this.formBuilder.group({
      property: this.createPropertyForm(draft?.property),
      objectOwner: this.createObjectOwnerForm(draft?.objectOwner),
      powerOfAttorneyInformation: this.createPowerOfAttorneyInformationForm(draft?.powerOfAttorneyInformation),
      user: this.createUserForm(draft?.user),
      mailVerification: this.createMailVerificationForm(draft?.mailVerification),
      billingAddress: this.createBillingAddressForm(draft?.billingAddress),
      entryParameters: this.createEntryParametersForm(draft?.entryParameters),
    });
  }

  /**
   * Creates the entry-parameter sub-form for URL parameters that started the booking flow.
   *
   * @param entryParameters Optional restored direct-booking entry parameters.
   */
  private createEntryParametersForm(entryParameters?: DirectBookingEntryParameters) {
    return this.formBuilder.group({
      coupon: this.formBuilder.control(entryParameters?.coupon ?? ''),
      packageUuid: this.formBuilder.control(entryParameters?.packageUuid ?? ''),
    });
  }

  /**
   * Writes mail verification values to the central form.
   *
   * @param verification Verification state that should be persisted in the order form.
   */
  private updateMailVerification(
    verification: MailVerificationDto,
  ): void {
    const normalizedMail = normalizeString(verification.email ?? '');

    this.mailVerificationForm.setValue(
      {
        id: verification.id ?? '',
        email: normalizedMail,
        status: verification.status ?? null,
      },
      {emitEvent: false},
    );
  }

  /**
   * Re-applies conditional object owner validators after silent form resets.
   */
  public refreshObjectOwnerValidators(): void {
    this.applyObjectOwnerTypeValidators();
  }

  /**
   * Re-applies conditional power-of-attorney validators after silent form resets.
   */
  public refreshPowerOfAttorneyValidators(): void {
    this.applyPowerOfAttorneyValidators();
  }

  /**
   * Creates the property sub-form including the nested land register structure.
   *
   * @param property Optional property values used to prefill the sub-form.
   */
  private createPropertyForm(property?: Property) {
    const value: Property = property ?? { landregisterfolios: [] };

    return this.formBuilder.group({
      id: this.formBuilder.control({ value: value.id ?? '', disabled: true }),
      zip: this.formBuilder.control(value.zip ?? ''),
      town: this.formBuilder.control(value.town ?? ''),
      street: this.formBuilder.control(value.street ?? ''),
      houseNumber: this.formBuilder.control(value.houseNumber ?? ''),
      state: this.formBuilder.control(value.state ?? ''),
      landregisterfolios: this.formBuilder.array(
        (value.landregisterfolios?.length ? value.landregisterfolios : [{ flure: [] }]).map(
          (landregisterfolio) => this.createLandregisterfolioForm(landregisterfolio),
        ),
      ),
    });
  }

  /**
   * Creates one land register folio form including its nested `Flur` entries.
   *
   * @param landregisterfolio Optional folio values used to prefill the sub-form.
   */
  private createLandregisterfolioForm(landregisterfolio?: Landregisterfolio) {
    const value: Landregisterfolio = landregisterfolio ?? { flure: [] };

    return this.formBuilder.group({
      id: this.formBuilder.control(value.id ?? ''),
      number: this.formBuilder.control(value.number ?? '', [containsDigitValidator]),
      gemarkung: this.formBuilder.control(value.gemarkung ?? ''),
      flure: this.formBuilder.array(
        (value.flure?.length ? value.flure : [{ flurstuecke: [] }]).map((flur) =>
          this.createFlurForm(flur),
        ),
      ),
    });
  }

  /**
   * Creates one `Flur` form including the nested `Flurstueck` array.
   *
   * @param flur Optional `Flur` values used to prefill the sub-form.
   */
  private createFlurForm(flur?: Flur) {
    const value: Flur = flur ?? { flurstuecke: [] };

    return this.formBuilder.group({
      id: this.formBuilder.control({ value: value.id ?? '', disabled: true }),
      number: this.formBuilder.control(value.number ?? ''),
      flurstuecke: this.formBuilder.array(
        (value.flurstuecke?.length ? value.flurstuecke : [{}]).map((flurstueck) =>
          this.createFlurstueckForm(flurstueck),
        ),
      ),
    });
  }

  /**
   * Creates one `Flurstueck` form.
   *
   * @param flurstueck Optional `Flurstueck` values used to prefill the sub-form.
   */
  private createFlurstueckForm(flurstueck?: Flurstueck) {
    const value: Flurstueck = flurstueck ?? {};

    return this.formBuilder.group({
      id: this.formBuilder.control({ value: value.id ?? '', disabled: true }),
      number: this.formBuilder.control(value.number ?? ''),
      geometry: this.formBuilder.control<ParcelGeometry | undefined>({
        value: value.geometry,
        disabled: true,
      }),
    });
  }

  /**
   * Filters one land register folio to the flure and parcels contained in the valid parcel set.
   *
   * @param folio Land register folio to filter.
   * @param parcelSet Valid parcel identifiers returned by the map.
   * @returns Filtered folio, or `null` when it no longer contains parcels.
   */
  private filterFolioByParcels(
    folio: Landregisterfolio,
    parcelSet: ReadonlySet<string>,
  ): Landregisterfolio | null {
    const flure = folio.flure
      .map((flur) => ({
        ...flur,
        flurstuecke: flur.flurstuecke.filter((flurstueck) =>
          this.parcelSetContainsFlurstueck(parcelSet, folio, flur, flurstueck),
        ),
      }))
      .filter((flur) => flur.flurstuecke.length > 0);

    if (!flure.length) {
      return null;
    }

    return { ...folio, flure };
  }

  /**
   * Removes one parcel from a folio and drops empty parent flure.
   *
   * @param folio Parent folio to update.
   * @param flurIndex Index of the parent flur.
   * @param flurstueckIndex Index of the parcel to remove.
   * @returns Updated folio, or `null` when no parcels remain in it.
   */
  private removeParcelFromFolio(
    folio: Landregisterfolio,
    flurIndex: number,
    flurstueckIndex: number,
  ): Landregisterfolio | null {
    const flure = folio.flure
      .map((flur, currentFlurIndex) => {
        if (currentFlurIndex !== flurIndex) {
          return flur;
        }

        return {
          ...flur,
          flurstuecke: flur.flurstuecke.filter(
            (_flurstueck, currentFlurstueckIndex) => currentFlurstueckIndex !== flurstueckIndex,
          ),
        };
      })
      .filter((flur) => flur.flurstuecke.length > 0);

    if (!flure.length) {
      return null;
    }

    return { ...folio, flure };
  }

  /**
   * Checks whether a flurstueck matches one of the parcel identifier formats returned by the map.
   *
   * @param parcelSet Valid parcel identifiers returned by the map.
   * @param folio Parent land register folio.
   * @param flur Parent flur.
   * @param flurstueck Parcel candidate to check.
   * @returns `true` when any supported parcel identifier format matches.
   */
  private parcelSetContainsFlurstueck(
    parcelSet: ReadonlySet<string>,
    folio: Landregisterfolio,
    flur: Flur,
    flurstueck: Flurstueck,
  ): boolean {
    const candidates = [
      flurstueck.number,
      [flur.number, flurstueck.number].filter(Boolean).join('-'),
      [folio.gemarkung, flur.number, flurstueck.number].filter(Boolean).join('_'),
      [folio.gemarkung, flur.number, flurstueck.number].filter(Boolean).join('-'),
      [folio.gemarkung, flur.number, flurstueck.number].filter(Boolean).join('/'),
    ].filter((candidate): candidate is string => !!candidate);

    return candidates.some((candidate) => parcelSet.has(candidate));
  }

  /**
   * Creates the object owner sub-form.
   *
   * @param objectOwner Optional object owner values used to prefill the sub-form.
   */
  private createObjectOwnerForm(objectOwner?: ObjectOwner) {
    const value = objectOwner ?? {};
    return this.formBuilder.group({
      id: this.formBuilder.control({ value: value.id ?? '', disabled: true }),
      firstname: this.formBuilder.control(value.firstname ?? ''),
      lastname: this.formBuilder.control(value.lastname ?? ''),
      birthName: this.formBuilder.control(value.birthName ?? ''),
      birthDate: this.formBuilder.control(value.birthDate ?? null),
      company: this.formBuilder.control(value.company ?? ''),
      objectOwnerType: this.formBuilder.control(value.objectOwnerType ?? null),
      isInheritanceCase: this.formBuilder.control(value.isInheritanceCase ?? null),
      selectedInheritanceCase: this.formBuilder.control(value.selectedInheritanceCase ?? null),
    });
  }

  /**
   * Creates the persisted mail verification sub-form.
   *
   * @param mailVerification Optional restored verification values used to prefill the sub-form.
   */
  private createMailVerificationForm(mailVerification?: MailVerificationDto) {
    const value = mailVerification ?? {id: '', email: '', status: null};

    return this.formBuilder.group({
      id: this.formBuilder.control(value.id ?? ''),
      email: this.formBuilder.control(normalizeString(value.email ?? '')),
      status: this.formBuilder.control<MailVerificationDto['status']>(value.status ?? null),
    });
  }

  /**
   * Creates the billing address sub-form that is persisted with the booking draft.
   *
   * @param billingAddress Optional billing address values restored from local storage.
   */
  private createBillingAddressForm(billingAddress?: BillingAddress) {
    const value = billingAddress ?? {};

    return this.formBuilder.group(
      {
        firma: this.formBuilder.control(value.firma ?? '', {
          validators: [meaningfulTextValidator, Validators.minLength(2), Validators.maxLength(455)],
        }),
        name: this.formBuilder.control(value.name ?? '', {
          validators: [meaningfulTextValidator, Validators.minLength(2), Validators.maxLength(455)],
        }),
        street: this.formBuilder.control(value.street ?? '', {
          validators: [
            Validators.required,
            nonBlankValidator,
            meaningfulTextValidator,
            Validators.maxLength(510),
          ],
        }),
        plz: this.formBuilder.control(value.plz ?? '', {
          validators: [
            Validators.required,
            countryPostalCodeValidator,
          ],
        }),
        town: this.formBuilder.control(value.town ?? '', {
          validators: [
            Validators.required,
            nonBlankValidator,
            meaningfulTextValidator,
            Validators.maxLength(250),
          ],
        }),
        country: this.formBuilder.control(value.country ?? '', {
          validators: [
            Validators.required,
            nonBlankValidator,
            meaningfulTextValidator,
            Validators.maxLength(255),
          ],
        }),
      },
      {
        validators: [atLeastOneNonBlankFieldValidator(['firma', 'name'])],
      },
    );
  }

  /**
   * Updates object owner field validators whenever the selected owner type changes.
   */
  private configureObjectOwnerTypeValidators(): void {
    this.applyObjectOwnerTypeValidators();

    this.objectOwnerForm.controls.objectOwnerType.valueChanges.subscribe(() => {
      this.applyObjectOwnerTypeValidators();
    });

    this.objectOwnerForm.controls.isInheritanceCase.valueChanges.subscribe(() => {
      this.applyObjectOwnerTypeValidators();
      this.applyPowerOfAttorneyValidators();
    });
  }

  /**
   * Applies the conditional required fields for private and company owners.
   */
  private applyObjectOwnerTypeValidators(): void {
    const controls = this.objectOwnerForm.controls;
    const nameValidators = [
      Validators.required,
      nonBlankValidator,
      personNameValidator,
      meaningfulTextValidator,
      Validators.minLength(2),
      Validators.maxLength(255),
    ];
    // The birth name is optional in the UI, so validate its format only when a value is present.
    const optionalNameValidators = [
      meaningfulTextValidator,
      Validators.minLength(2),
      Validators.maxLength(255),
    ];

    controls.firstname.setValidators([]);
    controls.lastname.setValidators([]);
    controls.birthName.setValidators(optionalNameValidators);
    controls.birthDate.setValidators([]);
    controls.company.setValidators([]);
    controls.isInheritanceCase.setValidators([]);
    controls.selectedInheritanceCase.setValidators([inheritanceDocumentsAvailableValidator]);

    controls.objectOwnerType.setValidators([Validators.required]);

    if (controls.objectOwnerType.value === ObjectOwnerType.PRIVATE) {
      controls.firstname.setValidators(nameValidators);
      controls.lastname.setValidators(nameValidators);
      controls.birthDate.setValidators([Validators.required, notInFutureDateValidator]);
      controls.isInheritanceCase.setValidators([Validators.required]);
    }

    if (controls.objectOwnerType.value === ObjectOwnerType.COMPANY) {
      controls.firstname.setValidators(nameValidators);
      controls.lastname.setValidators(nameValidators);
      controls.company.setValidators([
        Validators.required,
        nonBlankValidator,
        meaningfulTextValidator,
        Validators.minLength(2),
        Validators.maxLength(255),
      ]);
      controls.isInheritanceCase.setValidators([Validators.required]);
    }

    if (controls.isInheritanceCase.value === true) {
      controls.selectedInheritanceCase.setValidators([
        Validators.required,
        inheritanceDocumentsAvailableValidator,
      ]);
    }

    Object.values(controls).forEach((control) => {
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  /**
   * Creates the power-of-attorney sub-form including both uploaded key arrays.
   *
   * @param powerOfAttorneyInformation Optional power-of-attorney values used to prefill the sub-form.
   */
  private createPowerOfAttorneyInformationForm(
    powerOfAttorneyInformation?: PowerOfAttorneyInformation,
  ) {
    const value = powerOfAttorneyInformation ?? {
      uploadedAuthorityTemporaryUploadIds: [],
      uploadedAuthorityDocuments: [],
      uploadedInheritanceTemporaryUploadIds: [],
      uploadedInheritanceDocuments: [],
    };

    return this.formBuilder.group({
      uploadedAuthorityTemporaryUploadIds: this.formBuilder.array(
        (value.uploadedAuthorityTemporaryUploadIds?.length ? value.uploadedAuthorityTemporaryUploadIds : ['']).map((key) =>
          this.formBuilder.control(key),
        ),
      ),
      uploadedAuthorityDocuments: this.formBuilder.array(
        (value.uploadedAuthorityDocuments ?? []).map((document) =>
          this.createUploadedAuthorityDocumentForm(document),
        ),
      ),
      isPowerOfAttorneyAvailable: this.formBuilder.control(
        value.isPowerOfAttorneyAvailable ?? null,
      ),
      uploadedInheritanceTemporaryUploadIds: this.formBuilder.array(
        (value.uploadedInheritanceTemporaryUploadIds?.length ? value.uploadedInheritanceTemporaryUploadIds : ['']).map(
          (key) => this.formBuilder.control(key),
        ),
      ),
      uploadedInheritanceDocuments: this.formBuilder.array(
        (value.uploadedInheritanceDocuments ?? []).map((document) =>
          this.createUploadedAuthorityDocumentForm(document),
        ),
      ),
      submissionId: this.formBuilder.control(value.submissionId ?? ''),
      submissionUUID: this.formBuilder.control(value.submissionUUID ?? ''),
    });
  }

  /**
   * Creates one uploaded authority document metadata form.
   *
   * @param document Uploaded document metadata returned after a successful upload.
   */
  private createUploadedAuthorityDocumentForm(document: UploadedAuthorityDocument) {
    return this.formBuilder.group({
      temporaryUploadId: this.formBuilder.control(document.temporaryUploadId),
      name: this.formBuilder.control(document.name),
      size: this.formBuilder.control(document.size),
      contentType: this.formBuilder.control(document.contentType),
    });
  }

  /**
   * Updates power-of-attorney validators when the user changes the availability choice.
   */
  private configurePowerOfAttorneyValidators(): void {
    this.applyPowerOfAttorneyValidators();

    this.powerOfAttorneyInformationForm.controls.isPowerOfAttorneyAvailable.valueChanges.subscribe(
      () => {
        this.applyPowerOfAttorneyValidators();
      },
    );
  }

  /**
   * Requires a selected valid power of attorney and its uploaded authority file.
   */
  private applyPowerOfAttorneyValidators(): void {
    const controls = this.powerOfAttorneyInformationForm.controls;

    controls.isPowerOfAttorneyAvailable.setValidators([Validators.requiredTrue]);
    controls.uploadedAuthorityTemporaryUploadIds.setValidators([]);
    controls.uploadedInheritanceTemporaryUploadIds.setValidators([]);

    if (controls.isPowerOfAttorneyAvailable.value === true) {
      controls.uploadedAuthorityTemporaryUploadIds.setValidators([nonEmptyStringArrayValidator]);
    }

    if (this.objectOwnerForm.controls.isInheritanceCase.value === true) {
      controls.uploadedInheritanceTemporaryUploadIds.setValidators([nonEmptyStringArrayValidator]);
    }

    Object.values(controls).forEach((control) => {
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  /**
   * Replaces uploaded authority key controls with the provided keys.
   *
   * @param keys Authority document identifiers that should be persisted in the draft.
   */
  public setUploadedAuthorityTemporaryUploadIds(keys: readonly string[]): void {
    this.replaceStringArrayControls(
      this.powerOfAttorneyInformationForm.controls.uploadedAuthorityTemporaryUploadIds,
      keys,
    );
  }

  /**
   * Replaces uploaded authority metadata controls with the provided documents.
   *
   * @param documents Uploaded authority document metadata to persist in the draft.
   */
  public setUploadedAuthorityDocuments(documents: readonly UploadedAuthorityDocument[]): void {
    const array = this.powerOfAttorneyInformationForm.controls.uploadedAuthorityDocuments;

    array.clear({ emitEvent: false });
    documents.forEach((document) => {
      array.push(this.createUploadedAuthorityDocumentForm(document), { emitEvent: false });
    });

    array.updateValueAndValidity();
  }

  /**
   * Replaces uploaded inheritance document key controls with the provided keys.
   *
   * @param keys Inheritance document identifiers that should be persisted in the draft.
   */
  public setUploadedInheritanceTemporaryUploadIds(keys: readonly string[]): void {
    this.replaceStringArrayControls(
      this.powerOfAttorneyInformationForm.controls.uploadedInheritanceTemporaryUploadIds,
      keys,
    );
  }

  /**
   * Replaces uploaded inheritance metadata controls with the provided documents.
   *
   * @param documents Uploaded inheritance document metadata to persist in the draft.
   */
  public setUploadedInheritanceDocuments(documents: readonly UploadedAuthorityDocument[]): void {
    const array = this.powerOfAttorneyInformationForm.controls.uploadedInheritanceDocuments;

    array.clear({ emitEvent: false });
    documents.forEach((document) => {
      array.push(this.createUploadedAuthorityDocumentForm(document), { emitEvent: false });
    });

    array.updateValueAndValidity();
  }

  /**
   * Replaces a string form array while preserving at least one empty control for the DTO shape.
   *
   * @param array Form array to update.
   * @param values String values to write into the array.
   */
  private replaceStringArrayControls(
    array: FormArray<FormControl<string>>,
    values: readonly string[],
  ): void {
    array.clear({ emitEvent: false });

    const nextValues = values.length > 0 ? values : [''];
    nextValues.forEach((value) => {
      array.push(this.formBuilder.control(value), { emitEvent: false });
    });

    array.updateValueAndValidity();
  }

  /**
   * Creates the user sub-form.
   *
   * @param user Optional user values used to prefill the sub-form.
   */
  private createUserForm(user?: User) {
    const value = user ?? {};

    const form = this.formBuilder.group({
      id: this.formBuilder.control({ value: value.id ?? '', disabled: true }),
      company: this.formBuilder.control(value.company ?? '', [
        Validators.required,
        nonBlankValidator,
        meaningfulTextValidator,
        Validators.minLength(2),
        Validators.maxLength(210),
      ]),
      street: this.formBuilder.control(value.street ?? '', [
        Validators.required,
        nonBlankValidator,
        meaningfulTextValidator,
        Validators.maxLength(155),
      ]),
      houseNumber: this.formBuilder.control(value.houseNumber ?? '', [
        Validators.required,
        houseNumberValidator,
        Validators.maxLength(20),
      ]),
      zip: this.formBuilder.control(value.zip ?? '', [
        Validators.required,
        countryPostalCodeValidator,
      ]),
      town: this.formBuilder.control(value.town ?? '', [
        Validators.required,
        nonBlankValidator,
        meaningfulTextValidator,
        Validators.maxLength(90),
      ]),
      firstname: this.formBuilder.control(value.firstname ?? '', [
        Validators.required,
        nonBlankValidator,
        personNameValidator,
        meaningfulTextValidator,
        Validators.minLength(2),
        Validators.maxLength(45),
      ]),
      lastname: this.formBuilder.control(value.lastname ?? '', [
        Validators.required,
        nonBlankValidator,
        personNameValidator,
        meaningfulTextValidator,
        Validators.minLength(2),
        Validators.maxLength(45),
      ]),
      mail: this.formBuilder.control(value.mail ?? '', [
        Validators.required,
        nonBlankValidator,
        Validators.email,
        completeEmailDomainValidator,
        Validators.maxLength(90),
      ]),
      phone: this.formBuilder.control(value.phone ?? '', [
        Validators.required,
        nonBlankValidator,
        phoneNumberValidator,
      ]),
      country: this.formBuilder.control(value.country ?? DEFAULT_COUNTRY_NAME, [
        Validators.required,
        nonBlankValidator,
        meaningfulTextValidator,
        Validators.maxLength(255),
      ]),
    });

    form.controls.zip.updateValueAndValidity({emitEvent: false});

    return form;
  }
}
